/**
 * Activity Log Vuex Module
 *
 * Feature: 002-activity-log
 * Task: T003
 * Description: Vuex module for managing activity log state and Firestore interactions
 */
import { database } from "@/plugins/firebase";
import { seasonCollection } from "@/store/seasonYear";
const PAGE_SIZE = 500;
const createInitialState = () => ({
    entries: [],
    loading: false,
    error: null,
    hasMore: true,
    lastDoc: null,
    actionFilter: null,
    entityTypeFilter: null,
    dateRangeStart: null,
    dateRangeEnd: null
});
export const ActivityLogModule = {
    namespaced: true,
    state: createInitialState(),
    mutations: {
        setEntries(state, entries) {
            state.entries = entries;
        },
        appendEntries(state, entries) {
            state.entries = [...state.entries, ...entries];
        },
        setLoading(state, loading) {
            state.loading = loading;
        },
        setError(state, error) {
            state.error = error;
        },
        setHasMore(state, hasMore) {
            state.hasMore = hasMore;
        },
        setLastDoc(state, lastDoc) {
            state.lastDoc = lastDoc;
        },
        clear(state) {
            const initial = createInitialState();
            state.entries = initial.entries;
            state.loading = initial.loading;
            state.error = initial.error;
            state.hasMore = initial.hasMore;
            state.lastDoc = initial.lastDoc;
            state.actionFilter = initial.actionFilter;
            state.entityTypeFilter = initial.entityTypeFilter;
            state.dateRangeStart = initial.dateRangeStart;
            state.dateRangeEnd = initial.dateRangeEnd;
        },
        setActionFilter(state, filter) {
            state.actionFilter = filter;
        },
        setEntityTypeFilter(state, filter) {
            state.entityTypeFilter = filter;
        },
        setDateRange(state, { start, end }) {
            state.dateRangeStart = start;
            state.dateRangeEnd = end;
        }
    },
    actions: {
        async loadActivityLog(context, { reset = false } = {}) {
            const { commit, state } = context;
            // If reset, clear state first
            if (reset) {
                commit('clear');
            }
            // Don't load if already loading
            if (state.loading) {
                return;
            }
            // Don't load if no more entries (unless resetting)
            if (!reset && !state.hasMore) {
                return;
            }
            commit('setLoading', true);
            commit('setError', null);
            try {
                let query = database
                    .collection(seasonCollection('activity_log'))
                    .orderBy('timestamp', 'desc')
                    .limit(PAGE_SIZE);
                // If we have a lastDoc and not resetting, use it for pagination
                if (!reset && state.lastDoc) {
                    query = query.startAfter(state.lastDoc);
                }
                const snapshot = await query.get();
                const entries = [];
                snapshot.docs.forEach((doc) => {
                    const data = doc.data();
                    entries.push({
                        id: doc.id,
                        timestamp: data.timestamp,
                        userId: data.userId,
                        userName: data.userName,
                        action: data.action,
                        entityType: data.entityType,
                        entityId: data.entityId,
                        previousValue: data.previousValue,
                        newValue: data.newValue
                    });
                });
                // Update pagination state
                const hasMore = snapshot.docs.length === PAGE_SIZE;
                commit('setHasMore', hasMore);
                if (snapshot.docs.length > 0) {
                    commit('setLastDoc', snapshot.docs[snapshot.docs.length - 1]);
                }
                // Set or append entries based on reset flag
                if (reset) {
                    commit('setEntries', entries);
                }
                else {
                    commit('appendEntries', entries);
                }
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to load activity log';
                commit('setError', errorMessage);
            }
            finally {
                commit('setLoading', false);
            }
        }
    },
    getters: {
        entries(state) {
            return state.entries;
        },
        loading(state) {
            return state.loading;
        },
        error(state) {
            return state.error;
        },
        hasMore(state) {
            return state.hasMore;
        },
        filteredEntries(state) {
            let result = state.entries;
            if (state.actionFilter) {
                result = result.filter(e => e.action === state.actionFilter);
            }
            if (state.entityTypeFilter) {
                result = result.filter(e => e.entityType === state.entityTypeFilter);
            }
            if (state.dateRangeStart || state.dateRangeEnd) {
                result = result.filter(e => {
                    if (!e.timestamp)
                        return false;
                    const entryDate = e.timestamp.toDate();
                    const dateStr = entryDate.toISOString().split('T')[0];
                    if (state.dateRangeStart && dateStr < state.dateRangeStart) {
                        return false;
                    }
                    if (state.dateRangeEnd && dateStr > state.dateRangeEnd) {
                        return false;
                    }
                    return true;
                });
            }
            return result;
        }
    }
};
